<template lang="pug">
  div
    v-btn.round-md.px-2(color="white" depressed @click="openMenu")
      v-icon.mr-2(size=20 color="neutral500") $yzi-calendar-filter
      .text-sm.text-none(v-if="selectedOption") {{ radioGroup !== 'custom' ? selectedOption.name : dateText }}

    v-bottom-sheet(v-model="showMenu" max-width="100%")
      v-card
        v-card-text.pa-0
          .white
            // Radio button selection view
            div(v-if="!showCalendar")
              // Header - Fixed
              .d-flex.align-center.justify-space-between.pa-4.pb-2
                .text-lg.font-semibold Filter by date
                v-btn(icon @click="showMenu = false")
                  v-icon mdi-close

              // Single radio group for all options
              v-radio-group.ma-0(v-model="radioGroup" mandatory)
                // Scrollable radio button options
                .scrollable-options(style="max-height: 60vh; overflow-y: auto;")
                  .px-4.pb-2
                    div(v-for="option in regularOptions" :key="option.id")
                      .d-flex.align-center.justify-space-between.py-2(@click="selectOption(option.id)")
                        div
                          .text-base.font-medium {{ option.name }}
                          .text-sm.neutral500--text(v-if="option.description") {{ option.description }}
                        v-radio.ma-0(:value="option.id" color="primary")

                // Sticky custom option at bottom
                v-divider.basic300
                .px-4.pb-4.pt-2.custom-option-sticky
                  .d-flex.align-center.justify-space-between.py-2(@click="selectOption('custom')")
                    div
                      .text-base.font-medium {{ customOption.name }}
                      .text-sm.neutral500--text(v-if="radioGroup === 'custom'") {{ customOption.description }}
                    .d-flex.align-center
                      v-radio.ma-0(value="custom" color="primary")
                      v-icon mdi-chevron-right

            // Calendar view (only for custom range)
            div(v-else)
              // Header with back button
              .d-flex.align-center.justify-space-between.pa-4.pb-2
                .d-flex.align-center
                  v-btn(icon @click="showCalendar = false")
                    v-icon mdi-arrow-left
                  .text-lg.font-semibold.ml-2 Filter by date
                v-btn(icon @click="showMenu = false")
                  v-icon mdi-close

              // Selected date info
              .px-4.pb-2
                .text-base.font-medium {{ dateText }}
                .text-sm.primary--text.d-flex.align-center(v-if="selectedDates && selectedDates.length > 0")
                  v-icon.mr-1(size="16" color="primary") $yzi-info-circle
                  span Selected {{ getDayCount() }} {{ 'day' | pluralize({ count:getDayCount() })}} {{ todayInclusionText }}

              v-divider.basic300

              // Calendar
              .calendar-container(style="min-height: 320px;")
                yc-date-picker.range-date-picker(:parentDate.sync="selectedDates" range :readonly="readonly")

              // Quick action buttons
              .px-4.pb-6
                .d-flex.gap-2
                  v-btn(small outlined :class="{ 'primary white--text': radioGroup === 'lastSeven' }" @click="setQuickRange('lastSeven')") Last 7 days
                  v-btn(small outlined :class="{ 'primary white--text': radioGroup === 'lastThirty' }" @click="setQuickRange('lastThirty')") Last 30 days
                  v-btn(small outlined :class="{ 'primary white--text': radioGroup === 'thisWeek' }" @click="setQuickRange('thisWeek')") This week

              // Done button
              .px-4.pb-8
                v-btn(block color="primary" @click="showMenu = false") Done
</template>

<script>
import {DateTime} from 'luxon'
import {mapGetters} from 'vuex'

export default {
  name: 'RangeDropdownMobile',
  data: () => ({
    readonly: true,
    radioGroup: null,
    previousRadioGroup: null,
    selectedDates: null,
    showMenu: false,
    showCalendar: false,
    options: [],
  }),
  computed: {
    ...mapGetters({
      dates: 'insight/getDates',
      currentDateRange: 'insight/getCurrentDateRange'
    }),
    selectedOption() {
      let selected = this.options.find(i => i.id === this.radioGroup)
      if (selected) {
        return selected
      }
      return this.options[0]
    },
    dateText() {
      if (this.selectedDates && this.selectedDates.length > 0) {
        const start = this.selectedDates[0]
        let end = this.selectedDates[1]
        if (!end) {
          end = start
        }

        const startDateTime = DateTime.fromFormat(start, 'yyyy-MM-dd')
        const endDateTime = DateTime.fromFormat(end, 'yyyy-MM-dd')

        // If same date, just show single date
        if (start === end) {
          return startDateTime.toFormat('d MMM')
        }

        const startYear = startDateTime.year
        const endYear = endDateTime.year
        const startMonth = startDateTime.month
        const endMonth = endDateTime.month

        // Same year and month: "4 - 12 Jun"
        if (startYear === endYear && startMonth === endMonth) {
          const startDay = startDateTime.toFormat('d')
          const endFormatted = endDateTime.toFormat('d MMM')
          return `${startDay} - ${endFormatted}`
        }
        // Same year, different months: "4 Jun - 12 Aug"
        else if (startYear === endYear) {
          const startFormatted = startDateTime.toFormat('d MMM')
          const endFormatted = endDateTime.toFormat('d MMM')
          return `${startFormatted} - ${endFormatted}`
        }
        // Different years: "4 Jun 2025 - 12 Aug 2026"
        else {
          const startFormatted = startDateTime.toFormat('d MMM yyyy')
          const endFormatted = endDateTime.toFormat('d MMM yyyy')
          return `${startFormatted} - ${endFormatted}`
        }
      }
      return '-'
    },
    regularOptions() {
      return this.options.filter(option => option.id !== 'custom')
    },
    customOption() {
      const custom = this.options.find(option => option.id === 'custom')
      if (custom) {
        return {
          id: custom.id,
          name: custom.name,
          description: custom.description
        }
      }
      return {
        id: 'custom',
        name: 'Custom date range',
        description: null
      }
    },
    todayInclusionText() {
      if (!this.selectedDates || this.selectedDates.length === 0) {
        return ''
      }

      const today = DateTime.local().toFormat('yyyy-MM-dd')
      const startDate = this.selectedDates[0]
      const endDate = this.selectedDates[1] || this.selectedDates[0]

      // Check if today falls within the selected date range (inclusive)
      const todayDateTime = DateTime.fromFormat(today, 'yyyy-MM-dd')
      const startDateTime = DateTime.fromFormat(startDate, 'yyyy-MM-dd')
      const endDateTime = DateTime.fromFormat(endDate, 'yyyy-MM-dd')

      const isTodayIncluded = todayDateTime >= startDateTime && todayDateTime <= endDateTime

      return isTodayIncluded ? '(including today)' : '(excluding today)'
    }
  },
  watch: {
    radioGroup(val, oldVal) {
      this.previousRadioGroup = oldVal
      this.setDateRange(val)
    },
    selectedDates(val, oldVal) {
      if (oldVal !== val) {
        this.setNewDates(val)
        // Update custom option description when dates change
        if (this.radioGroup === 'custom') {
          this.generateOptions()
        }
      }
    },
    showMenu(val) {
      if (!val) {
        setTimeout(() => {
          this.showCalendar = false
        }, 250)
        this.previousRadioGroup = this.radioGroup
      }
    },
    showCalendar(val) {
      if (val) {
        // Only reset to today's date if previous selection was not custom
        // Keep existing dates if already on custom selection
        if (this.previousRadioGroup !== 'custom') {
          const now = DateTime.local()
          const dateFormat = 'yyyy-MM-dd'
          this.selectedDates = [now.toFormat(dateFormat), now.toFormat(dateFormat)]
        }
      } else {
        this.getCustomDescription()
      }
    }
  },
  mounted() {
    this.generateOptions()
    this.selectedDates = this.dates || []
    this.radioGroup = this.currentDateRange?.id || 'yesterday'
  },
  methods: {
    setDateRange(val) {
      const now = DateTime.local()
      const dateFormat = 'yyyy-MM-dd'
      this.readonly = true
      switch (val) {
        case 'yesterday': {
          const start = now.minus({days: 1})
          this.selectedDates = [start.toFormat(dateFormat), start.toFormat(dateFormat)]
          break
        }
        case 'lastSeven': {
          const start = now.minus({days: 6})
          this.selectedDates = [start.toFormat(dateFormat), now.toFormat(dateFormat)]
          break
        }
        case 'lastThirty': {
          const start = now.minus({days: 30})
          this.selectedDates = [start.toFormat(dateFormat), now.toFormat(dateFormat)]
          break
        }
        case 'lastNinety': {
          const start = now.minus({days: 90})
          this.selectedDates = [start.toFormat(dateFormat), now.toFormat(dateFormat)]
          break
        }
        case 'thisWeek': {
          const start = now.startOf('week')
          const end = now.endOf('week')
          this.selectedDates = [start.toFormat(dateFormat), end.toFormat(dateFormat)]
          break
        }
        case 'thisMonth': {
          const start = now.startOf('month')
          const end = now.endOf('month')
          this.selectedDates = [start.toFormat(dateFormat), end.toFormat(dateFormat)]
          break
        }
        case 'thisYear': {
          const start = now.startOf('year')
          const end = now.endOf('year')
          this.selectedDates = [start.toFormat(dateFormat), end.toFormat(dateFormat)]
          break
        }
        case 'lastWeek': {
          const lastWeek = now.minus({weeks: 1})
          const lastWeekStart = lastWeek.startOf('week')
          const lastWeekEnd = lastWeek.endOf('week')
          this.selectedDates = [lastWeekStart.toFormat(dateFormat), lastWeekEnd.toFormat(dateFormat)]
          break
        }
        case 'lastMonth': {
          const lastMonth = now.minus({months: 1})
          const lastMonthStart = lastMonth.startOf('month')
          const lastMonthEnd = lastMonth.endOf('month')
          this.selectedDates = [lastMonthStart.toFormat(dateFormat), lastMonthEnd.toFormat(dateFormat)]
          break
        }
        case 'custom': {
          this.readonly = false
          break
        }
        default:
          break
      }
    },
    setNewDates(val) {
      let newValue = val
      if (!val[1]) {
        newValue = [val[0], val[0]]
      }
      newValue = newValue.sort()
      this.$store.commit('insight/setBeginDate', newValue[0])
      this.$store.commit('insight/setUntilDate', newValue[1])
      this.$store.commit('insight/setDates', val)
      this.$store.commit('insight/setCurrentDateRange', this.selectedOption)
    },
    generateOptions() {
      const now = DateTime.local()
      const dateFormat = 'd MMM'

      // Regular options (non-custom)
      const regularOptions = [
        {
          id: 'yesterday',
          name: 'Yesterday',
          description: now.minus({days: 1}).toFormat(dateFormat)
        },
        {
          id: 'lastSeven',
          name: 'Last 7 days',
          description: `${now.minus({days: 6}).toFormat(dateFormat)} - ${now.toFormat(dateFormat)}`
        },
        {
          id: 'lastThirty',
          name: 'Last 30 days',
          description: `${now.minus({days: 29}).toFormat(dateFormat)} - ${now.toFormat(dateFormat)}`
        },
        {
          id: 'lastNinety',
          name: 'Last 90 days',
          description: `${now.minus({days: 89}).toFormat(dateFormat)} - ${now.toFormat(dateFormat)}`
        },
        {
          id: 'thisWeek',
          name: 'This week',
          description: `${now.startOf('week').toFormat(dateFormat)} - ${now.endOf('week').toFormat(dateFormat)}`
        },
        {
          id: 'thisMonth',
          name: 'This month',
          description: `${now.startOf('month').toFormat(dateFormat)} - ${now.endOf('month').toFormat(dateFormat)}`
        },
        {
          id: 'thisYear',
          name: 'This year',
          description: `${now.startOf('year').toFormat(dateFormat)} - ${now.endOf('year').toFormat(dateFormat)}`
        },
        {
          id: 'lastWeek',
          name: 'Last week',
          description: `${now.minus({weeks: 1}).startOf('week').toFormat(dateFormat)} - ${now.minus({weeks: 1}).endOf('week').toFormat(dateFormat)}`
        },
        {
          id: 'lastMonth',
          name: 'Last month',
          description: `${now.minus({months: 1}).startOf('month').toFormat(dateFormat)} - ${now.minus({months: 1}).endOf('month').toFormat(dateFormat)}`
        }
      ]

      // Custom option with dynamic description
      const customOption = {
        id: 'custom',
        name: 'Custom date range',
        description: this.getCustomDescription()
      }

      this.options = [...regularOptions, customOption]
    },
    getCustomDescription() {
      if (this.selectedDates && this.selectedDates.length > 0 && this.radioGroup === 'custom') {
        const start = this.selectedDates[0]
        const end = this.selectedDates[1] || this.selectedDates[0]
        const dateFormat = 'd MMM'
        const startDate = DateTime.fromFormat(start, 'yyyy-MM-dd').toFormat(dateFormat)
        const endDate = DateTime.fromFormat(end, 'yyyy-MM-dd').toFormat(dateFormat)
        return `${startDate} - ${endDate}`
      }
      return null
    },
    getDayCount() {
      if (this.selectedDates && this.selectedDates.length > 0) {
        const start = DateTime.fromFormat(this.selectedDates[0], 'yyyy-MM-dd')
        const end = DateTime.fromFormat(this.selectedDates[1] || this.selectedDates[0], 'yyyy-MM-dd')
        return Math.abs(end.diff(start, 'days').days) + 1
      }
      return 0
    },
    setQuickRange(rangeType) {
      this.radioGroup = rangeType
    },
    selectOption(optionId) {
      this.radioGroup = optionId
      if (optionId === 'custom') {
        this.showCalendar = true
      } else {
        this.showCalendar = false
        this.showMenu = false
      }
    },
    openMenu() {
      this.showMenu = true
    }
  }
}
</script>

<style lang="scss">
.range-date-picker {
  .v-date-picker-table .v-btn.v-date-picker-table__current {
    color: #333333 !important;
  }

  .v-date-picker-table .v-btn.v-btn--active {
    background-color: #EEEEEE !important;
    color: #333333 !important;
  }

  .v-date-picker-table .v-btn.v-btn--active.v-date-picker--first-in-range, .v-btn.v-btn--active.v-date-picker--last-in-range {
    background-color: #333333 !important;

    .v-btn__content {
      color: #ffffff;
    }
  }

  .v-date-picker-table .v-btn {
    font-weight: 500;
    font-size: 16px !important;
  }

  .v-date-picker-table__current {
    border: 2px solid black !important;
  }
}

// Additional styling for the new layout
.v-radio-group .v-radio {
  .v-input--selection-controls__input {
    margin-right: 0 !important;
  }
}

.gap-2 {
  gap: 8px;
}

.calendar-container {
  .v-date-picker {
    width: 100% !important;
    box-shadow: none !important;
  }
}

.scrollable-options {
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
  }
}

.custom-option-sticky {
  background-color: white;
  border-top: 1px solid #e0e0e0;
}
</style>